import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import HomePage from "../../business/LandingPage/components/LandingPage";
import LanguageProvider from "../components/LanguageProvider";
import LoadingIndicator from "../components/LoadingIndicator";

// Component to handle language detection and redirection
const LanguageRedirect: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = React.useState(true);

  useEffect(() => {
    // Function to detect the user's preferred language
    const detectUserLanguage = (): string => {
      // First, check localStorage for a saved preference
      const savedLang = localStorage.getItem("i18nextLng");
      if (savedLang && (savedLang === "en" || savedLang === "ar")) {
        return savedLang;
      }

      // Next, check browser language settings
      const browserLang = navigator.language.split("-")[0].toLowerCase();
      if (browserLang === "ar") {
        return "ar";
      }

      // Default to English for all other cases
      return "en";
    };

    // Check if we have a redirect path from the 404 page
    const redirectPath = sessionStorage.getItem("redirectPath");
    if (redirectPath) {
      // Clear the redirect path from session storage
      sessionStorage.removeItem("redirectPath");

      // Extract language from the redirect path if possible
      const pathSegments = redirectPath.split("/").filter(Boolean);
      if (
        pathSegments.length > 0 &&
        (pathSegments[0] === "en" || pathSegments[0] === "ar")
      ) {
        // If the path already has a valid language prefix, just navigate to it
        navigate(redirectPath, { replace: true });
        setIsLoading(false);
        return;
      }
    }

    // Only redirect if we're at the root path or an invalid path
    if (
      location.pathname === "/" ||
      (location.pathname !== "/en" &&
        location.pathname !== "/ar" &&
        !location.pathname.startsWith("/en/") &&
        !location.pathname.startsWith("/ar/"))
    ) {
      // Get the user's preferred language
      const userLang = detectUserLanguage();

      // If we have a redirect path that doesn't have a language prefix, add it
      if (redirectPath && redirectPath !== "/") {
        navigate(`/${userLang}${redirectPath}`, { replace: true });
      } else {
        // Otherwise, just navigate to the language root
        navigate(`/${userLang}${location.search}`, { replace: true });
      }
    }

    setIsLoading(false);
  }, [navigate, location]);

  // Show a loading indicator while redirecting
  if (isLoading) {
    return <LoadingIndicator message="Detecting language..." />;
  }

  return null;
};

// Component to wrap HomePage with language provider
const LanguageSpecificPage: React.FC = () => {
  return (
    <LanguageProvider>
      <HomePage />
    </LanguageProvider>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Root path redirects to language-specific path */}
        <Route path="/" element={<LanguageRedirect />} />

        {/* Language-specific routes */}
        <Route path="/en/*" element={<LanguageSpecificPage />} />
        <Route path="/ar/*" element={<LanguageSpecificPage />} />

        {/* Handle direct access to sections */}
        <Route path="/home" element={<LanguageRedirect />} />
        <Route path="/about" element={<LanguageRedirect />} />
        <Route path="/services" element={<LanguageRedirect />} />
        <Route path="/contact" element={<LanguageRedirect />} />

        {/* Fallback route for any other path */}
        <Route path="*" element={<LanguageRedirect />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
